import { IEntity, SafeAny } from '@pf/shared-common';
import { Operation } from 'fast-json-patch';

/**
 * Adjusts a list of JSON Patch operations to handle specific cases for add and remove operations.
 *
 * The function performs the following operations:
 * 1. Filters out 'add' operations with null values.
 * 2. Adds 'remove' operations for sub-entities marked as deleted in the DTO.
 * 3. Collapses any array patch operation into a single operation for the entire array.
 *    If the array already existed in the original entity this is a 'replace' with the final
 *    array value; otherwise it is an 'add'. This avoids backend problems with individual
 *    add/remove indices and the JSON Patch "-" append token.
 * 4. Sorts 'remove' operations to the end of the operations list.
 *
 * @param original - The original entity before the update.
 * @param dto - The updated data transfer object that contains the entity data.
 * @param ops - The list of JSON Patch operations to be adjusted.
 * @returns The adjusted list of JSON Patch operations.
 *
 * @example
 * const original = {
 *   items: [{ isDeleted: true }, { isDeleted: false }]
 * };
 * const dto = {
 *   items: [{ isDeleted: true }, { isDeleted: false }]
 * };
 * const ops = [
 *   { op: 'add', path: '/items/0', value: { id: 1 } },
 *   { op: 'add', path: '/items/1', value: null }
 * ];
 * const result = fixEntityOperations(original, dto, ops);
 * // result will be:
 * // [
 * //   { op: 'replace', path: '/items', value: [{ isDeleted: false }] }
 * // ]
 */
export function fixEntityOperations(dto: SafeAny, ops: Operation[]) {
    let updatedOps = ops.filter(o => o.op !== 'add' || o.value !== null);

    function fixDeleteOps() {
        Object.keys(dto).forEach(key => {
            if (!Array.isArray(dto[key])) {
                return;
            }
            (dto[key] as IEntity[]).forEach((subEntity, subEntityIndex) => {
                if (subEntity.isDeleted) {
                    const path = `/${key}/${subEntityIndex}`;
                    updatedOps = updatedOps.filter(
                        o => !o.path.startsWith(path)
                    );
                    updatedOps.unshift({
                        op: 'remove',
                        path,
                    } as Operation);
                }
            });
        });
    }

    function collapseArrayOperations() {
        const keysToCollapse = new Set<string>();
        updatedOps.forEach(o => {
            const path = o.path.split('/');
            const key = path[1];
            if (!key || !Array.isArray(dto[key])) {
                return;
            }
            if (o.path === `/${key}` || o.path.startsWith(`/${key}/`)) {
                keysToCollapse.add(key);
            }
        });

        keysToCollapse.forEach(key => {
            updatedOps = updatedOps.filter(o => {
                const pathSegments = o.path.split('/');
                return pathSegments[1] !== key;
            });

            const finalArray = (dto[key] as IEntity[]).filter(
                item => !item.isDeleted
            );
            updatedOps.push({
                op: 'replace',
                path: `/${key}`,
                value: finalArray,
            } as Operation);
        });
    }

    fixDeleteOps();
    collapseArrayOperations();
    // sort operations so array delete operations are done in reverse order and after all other operations
    return updatedOps.sort((a, b) => {
        if (a.op === 'remove') {
            return 1;
        }
        if (b.op === 'remove') {
            return -1;
        }
        return 0;
    });
}
